import classNames from 'classnames';

import { Panel, PanelHeader } from './panel';
import {
  formatDelta,
  formatNumber,
  NO_VALUE
} from '@lib/page-utils/player-profile/format';

/** Deltas below this are rounding noise, not over- or underperformance. */
const DELTA_EPSILON = 0.005;

export type PerformanceDelta = {
  label: string;
  expectedLabel: string;
  actual: number | null;
  expected: number | null;
};

type PerformanceDeltasProps = {
  deltas: PerformanceDelta[];
  decimals: number;
};

const DeltaRow = ({
  delta,
  decimals
}: {
  delta: PerformanceDelta;
  decimals: number;
}) => {
  const { actual, expected } = delta;
  const hasBoth = actual !== null && expected !== null;
  const difference = hasBoth ? actual - expected : null;
  const isLevel = difference !== null && Math.abs(difference) < DELTA_EPSILON;
  // A level delta is neither over nor under. Deriving `isOver` from the sign
  // alone would colour a dead-level player as underperforming, contradicting
  // the "As expected" chip sitting directly above the bar.
  const isOver = difference !== null && !isLevel && difference > 0;
  const isUnder = difference !== null && !isLevel && difference < 0;

  const scale = Math.max(actual ?? 0, expected ?? 0, 1);
  const actualWidth = actual === null ? 0 : (actual / scale) * 100;
  const expectedOffset = expected === null ? null : (expected / scale) * 100;

  return (
    <div className="border-t border-neutral-200 px-5 py-3.5 first:border-t-0 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] font-semibold text-neutral-700">
          {delta.label}
        </span>
        {difference !== null && !isLevel && (
          <span
            className={classNames(
              'rounded-md px-2 py-0.5 text-xs font-bold tabular-nums',
              isOver
                ? 'bg-success-50 text-success-700'
                : 'bg-danger-50 text-danger-700'
            )}
          >
            {isOver ? '↑' : '↓'} {formatDelta(difference, decimals)}
          </span>
        )}
        {isLevel && (
          <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-bold text-neutral-600">
            As expected
          </span>
        )}
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-sans text-[30px] font-bold tabular-nums leading-none text-neutral-900">
          {formatNumber(actual, decimals)}
        </span>
        <span className="text-[13px] text-neutral-500">
          vs {formatNumber(expected, decimals)} {delta.expectedLabel}
        </span>
      </div>

      {hasBoth && (
        <div className="relative mt-3 h-1.5 rounded-full bg-neutral-100">
          <div
            className={classNames(
              'h-full rounded-full',
              isOver && 'bg-success-500',
              isUnder && 'bg-danger-400',
              isLevel && 'bg-neutral-400'
            )}
            style={{ width: `${Math.min(actualWidth, 100)}%` }}
          />
          {expectedOffset !== null && (
            <span
              aria-hidden
              className="absolute top-0 h-full w-0.5 bg-neutral-800"
              style={{ left: `${Math.min(expectedOffset, 100)}%` }}
            />
          )}
        </div>
      )}

      {!hasBoth && (
        <p className="mt-2 text-xs text-neutral-400">
          {NO_VALUE} No data for these filters
        </p>
      )}
    </div>
  );
};

export const PerformanceDeltas = ({
  deltas,
  decimals
}: PerformanceDeltasProps) => (
  <Panel className="flex h-full flex-col">
    <PanelHeader
      title="Over / underperformance"
      subtitle="Returns against what the underlying numbers expected"
    />
    <div className="flex-1 border-t border-neutral-200">
      {deltas.map((delta) => (
        <DeltaRow key={delta.label} delta={delta} decimals={decimals} />
      ))}
    </div>
  </Panel>
);
